import type {DOMAttributes} from '@react-types/shared';
import type {ReactNode} from 'react';
import {useId} from '../utils';

export interface AriaListBoxSectionProps {
  /** The heading for the section. */
  heading?: ReactNode;
  /** An accessibility label for the section. Required if `heading` is not present. */
  'aria-label'?: string;
}

export interface ListBoxSectionAria {
  /** Props for the wrapper list item. */
  itemProps: DOMAttributes;

  /** Props for the heading element, if any. */
  headingProps: DOMAttributes;

  /** Props for the group element. */
  groupProps: DOMAttributes;
}

/**
 * Provides the behavior and accessibility implementation for a section in a listbox.
 * See `useListBox` for more details about listboxes.
 * @param props - Props for the section.
 */
export function useListBoxSection(
  props: AriaListBoxSectionProps,
): ListBoxSectionAria {
  const {heading, 'aria-label': ariaLabel} = props;
  const headingId = useId();

  return {
    itemProps: {
      role: 'presentation',
    },
    headingProps: heading
      ? {
          // Techincally, listbox cannot contain headings according to ARIA.
          // We hide the heading from assistive technology, using role="presentation",
          // and only use it as a visual label for the nested group.
          id: headingId,
          role: 'presentation',
        }
      : {},
    groupProps: {
      role: 'group',
      'aria-label': ariaLabel,
      'aria-labelledby': heading ? headingId : undefined,
    },
  };
}
